import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  KeyboardEvent,
} from 'react'
import { useId, useRef } from 'react'

interface UseDetailTabsOptions<Tab extends string> {
  activeTab: Tab
  onTabChange: (tab: Tab) => void
  tabs: readonly Tab[]
}

type DetailTabProps = Pick<
  ButtonHTMLAttributes<HTMLButtonElement>,
  | 'aria-controls'
  | 'aria-selected'
  | 'id'
  | 'onClick'
  | 'onKeyDown'
  | 'role'
  | 'tabIndex'
  | 'type'
> & {
  ref: (element: HTMLButtonElement | null) => void
}

type DetailTabPanelProps = Pick<
  HTMLAttributes<HTMLElement>,
  'aria-labelledby' | 'hidden' | 'id' | 'role' | 'tabIndex'
>

const useDetailTabs = <Tab extends string>({
  activeTab,
  onTabChange,
  tabs,
}: UseDetailTabsOptions<Tab>): {
  getPanelProps: (tab: Tab) => DetailTabPanelProps
  getTabProps: (tab: Tab) => DetailTabProps
} => {
  const id = useId()
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([])

  const selectTabFromKeyboard = (
    event: KeyboardEvent<HTMLButtonElement>,
    currentIndex: number,
  ): void => {
    let nextIndex: number | undefined

    switch (event.key) {
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + tabs.length) % tabs.length
        break
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % tabs.length
        break
      case 'Home':
        nextIndex = 0
        break
      case 'End':
        nextIndex = tabs.length - 1
        break
      default:
        return
    }

    event.preventDefault()
    onTabChange(tabs[nextIndex])
    tabRefs.current[nextIndex]?.focus()
  }

  const getTabProps = (tab: Tab): DetailTabProps => {
    const index = tabs.indexOf(tab)
    const selected = tab === activeTab
    return {
      id: `${id}-${tab}-tab`,
      type: 'button',
      role: 'tab',
      'aria-controls': `${id}-${tab}-panel`,
      'aria-selected': selected,
      tabIndex: selected ? 0 : -1,
      ref: (element) => {
        tabRefs.current[index] = element
      },
      onClick: (event) => {
        event.currentTarget.focus()
        onTabChange(tab)
      },
      onKeyDown: (event) => selectTabFromKeyboard(event, index),
    }
  }

  const getPanelProps = (tab: Tab): DetailTabPanelProps => ({
    id: `${id}-${tab}-panel`,
    role: 'tabpanel',
    'aria-labelledby': `${id}-${tab}-tab`,
    hidden: tab !== activeTab,
    tabIndex: 0,
  })

  return { getPanelProps, getTabProps }
}

export default useDetailTabs
